/**
 * Rider Network — Stage 3 deliverable.
 *
 * Aggregates rider rosters from the existing per-store endpoint
 * (`/api/stores/online/{id}/`) into a single network view. This avoids
 * a new BI endpoint per workspace rule §1; it does mean N queries but
 * React Query caches each store individually and refetches on a
 * 60-second cadence so the per-store detail pages stay warm too.
 */
import { useMemo, useState } from 'react';
import {
  Bike,
  CheckCircle2,
  CircleDashed,
  Search,
  Store as StoreIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Input } from '@/components/ui/Input';
import {
  useStoreDetails,
  useStoresList,
  type StoreStaffRow,
} from '@/api/legacy/stores';
import { cn } from '@/lib/cn';
import { openRiderOrders } from '@/state/riderOrdersDialog';

interface RiderRow extends StoreStaffRow {
  store_id: number;
  store_name: string;
}

export function RidersPage() {
  const stores = useStoresList();
  const ids = useMemo(() => (stores.data ?? []).map((s) => s.id), [stores.data]);
  const details = useStoreDetails(ids);

  const [search, setSearch] = useState('');
  const [onlyOnline, setOnlyOnline] = useState(true);

  const isLoading = stores.isLoading || details.some((q) => q.isLoading);
  const isError = stores.isError || details.some((q) => q.isError && !q.data);

  const rows: RiderRow[] = useMemo(() => {
    const acc: RiderRow[] = [];
    details.forEach((q) => {
      const d = q.data;
      if (!d) return;
      for (const r of d.rider_status_list ?? []) {
        acc.push({ ...r, store_id: d.id, store_name: d.name });
      }
    });
    return acc;
  }, [details]);

  const filtered = useMemo(() => {
    let r = rows;
    if (onlyOnline) r = r.filter((x) => x.is_online);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter((x) => x.name.toLowerCase().includes(q) || x.store_name.toLowerCase().includes(q));
    }
    return r.sort((a, b) => Number(b.is_online) - Number(a.is_online) || a.name.localeCompare(b.name));
  }, [rows, search, onlyOnline]);

  const groupedByStore = useMemo(() => {
    const m = new Map<number, { store: string; riders: RiderRow[] }>();
    for (const r of filtered) {
      const cur = m.get(r.store_id) ?? { store: r.store_name, riders: [] };
      cur.riders.push(r);
      m.set(r.store_id, cur);
    }
    return Array.from(m.entries()).sort((a, b) => a[1].store.localeCompare(b[1].store));
  }, [filtered]);

  const onlineCount = rows.filter((r) => r.is_online).length;

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Rider Network"
        description="Every rider across every store, at a glance. Pulled from the live store rosters."
        actions={
          <div className="flex items-center gap-1.5">
            <a
              href="/riders/distribution"
              className="press inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Bike className="h-3.5 w-3.5" /> Workload
            </a>
            <Badge tone={onlineCount > 0 ? 'success' : 'neutral'}>
              <Bike className="h-3 w-3" /> {onlineCount} online · {rows.length} total
            </Badge>
          </div>
        }
      />

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Riders</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find a rider or store…"
                className="h-8 w-56 pl-7 text-xs"
              />
            </div>
            <button
              type="button"
              onClick={() => setOnlyOnline((v) => !v)}
              className={cn(
                'press inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 text-xs focus-ring',
                onlyOnline ? 'text-success' : 'text-muted-foreground',
              )}
            >
              <span aria-hidden className={cn('inline-block h-1.5 w-1.5 rounded-full', onlyOnline ? 'bg-success' : 'bg-muted-foreground/40')} />
              Online only
            </button>
          </div>
        </CardHeader>
        <CardBody>
          {isError ? (
            <ErrorState onRetry={() => details.forEach((q) => void q.refetch())} />
          ) : isLoading && rows.length === 0 ? (
            <Skeleton className="h-32 w-full" />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<Bike className="h-5 w-5" />}
              title="No riders match these filters"
              description={onlyOnline ? 'Try toggling "Online only" off to see returning/offline riders.' : undefined}
            />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {groupedByStore.map(([id, group]) => (
                <Card key={id} className="border-border/80">
                  <CardHeader>
                    <CardTitle className="inline-flex items-center gap-2 text-sm">
                      <StoreIcon className="h-3.5 w-3.5 text-muted-foreground" />
                      {group.store}
                    </CardTitle>
                    <Badge tone="neutral">{group.riders.length}</Badge>
                  </CardHeader>
                  <CardBody className="px-0 pb-2">
                    <ul className="divide-y divide-border">
                      {group.riders.map((r) => (
                        <li key={`${r.store_id}-${r.id}`} className="flex items-center justify-between gap-2 px-5 py-2 text-sm">
                          <span className="inline-flex min-w-0 items-center gap-2">
                            {r.is_online ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />
                            ) : (
                              <CircleDashed className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden />
                            )}
                            <span className="min-w-0">
                              <button
                                type="button"
                                onClick={() =>
                                  openRiderOrders({
                                    riderId: r.id,
                                    riderName: r.name,
                                    storeId: r.store_id,
                                  })
                                }
                                className="block max-w-full truncate text-left font-medium text-foreground underline-offset-2 hover:text-info hover:underline focus-ring rounded-sm"
                              >
                                {r.name}
                              </button>
                              {r.uses_cycle ? (
                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">cycle</span>
                              ) : null}
                            </span>
                          </span>
                          <span className="max-w-[140px] truncate text-[11px] text-muted-foreground" title={r.status}>
                            {r.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
